import { useState } from "react";
import { Field, TextInput, Select, PrimaryButton, ResultStat } from "@/tools/ui";
export const BMICalculator = () => {
  const [w, setW] = useState("");
  const [h, setH] = useState("");
  const [res, setRes] = useState(null);

  const calc = () => {
    const weight = parseFloat(w);
    const height = parseFloat(h) / 100;
    if (!weight || !height) return setRes(null);
    const bmi = weight / (height * height);
    let cat = "Normal";
    if (bmi < 18.5) cat = "Underweight";
    else if (bmi < 25) cat = "Normal";
    else if (bmi < 30) cat = "Overweight";
    else cat = "Obese";
    setRes({ bmi: bmi.toFixed(1), cat });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Weight (kg)"><TextInput data-testid="bmi-weight" type="number" value={w} onChange={(e) => setW(e.target.value)} placeholder="70" /></Field>
        <Field label="Height (cm)"><TextInput data-testid="bmi-height" type="number" value={h} onChange={(e) => setH(e.target.value)} placeholder="175" /></Field>
      </div>
      <PrimaryButton data-testid="bmi-calc-btn" onClick={calc}>Calculate BMI</PrimaryButton>
      {res && (
        <div data-testid="bmi-result" className="grid grid-cols-2 gap-4">
          <ResultStat label="Your BMI" value={res.bmi} accent />
          <ResultStat label="Category" value={res.cat} />
        </div>
      )}
    </div>
  );
};

export const EMICalculator = () => {
  const [p, setP] = useState("");
  const [r, setR] = useState("");
  const [n, setN] = useState("");
  const [res, setRes] = useState(null);

  const calc = () => {
    const principal = parseFloat(p);
    const rate = parseFloat(r) / 12 / 100;
    const months = parseFloat(n) * 12;
    if (!principal || !rate || !months) return setRes(null);
    const emi = (principal * rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
    const total = emi * months;
    const fmt = (x) => x.toLocaleString(undefined, { maximumFractionDigits: 0 });
    setRes({ emi: fmt(emi), total: fmt(total), interest: fmt(total - principal) });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Loan Amount"><TextInput data-testid="emi-principal" type="number" value={p} onChange={(e) => setP(e.target.value)} placeholder="500000" /></Field>
        <Field label="Interest Rate (% / yr)"><TextInput data-testid="emi-rate" type="number" value={r} onChange={(e) => setR(e.target.value)} placeholder="8.5" /></Field>
        <Field label="Tenure (years)"><TextInput data-testid="emi-tenure" type="number" value={n} onChange={(e) => setN(e.target.value)} placeholder="5" /></Field>
      </div>
      <PrimaryButton data-testid="emi-calc-btn" onClick={calc}>Calculate EMI</PrimaryButton>
      {res && (
        <div data-testid="emi-result" className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ResultStat label="Monthly EMI" value={res.emi} accent />
          <ResultStat label="Total Interest" value={res.interest} />
          <ResultStat label="Total Payment" value={res.total} />
        </div>
      )}
    </div>
  );
};

export const AgeCalculator = () => {
  const [dob, setDob] = useState("");
  const [res, setRes] = useState(null);

  const calc = () => {
    if (!dob) return setRes(null);
    const birth = new Date(dob);
    const now = new Date();
    if (birth > now) return setRes(null);
    let years = now.getFullYear() - birth.getFullYear();
    let months = now.getMonth() - birth.getMonth();
    let days = now.getDate() - birth.getDate();
    if (days < 0) { months--; days += new Date(now.getFullYear(), now.getMonth(), 0).getDate(); }
    if (months < 0) { years--; months += 12; }
    const totalDays = Math.floor((now - birth) / (1000 * 60 * 60 * 24));
    setRes({ years, months, days, totalDays: totalDays.toLocaleString() });
  };

  return (
    <div className="space-y-6">
      <Field label="Date of birth"><TextInput data-testid="age-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} /></Field>
      <PrimaryButton data-testid="age-calc-btn" onClick={calc}>Calculate Age</PrimaryButton>
      {res && (
        <div data-testid="age-result" className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <ResultStat label="Years" value={res.years} accent />
          <ResultStat label="Months" value={res.months} />
          <ResultStat label="Days" value={res.days} />
          <ResultStat label="Total Days" value={res.totalDays} />
        </div>
      )}
    </div>
  );
};

export const PercentageCalculator = () => {
  const [mode, setMode] = useState("of");
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [res, setRes] = useState(null);

  const calc = () => {
    const x = parseFloat(a);
    const y = parseFloat(b);
    if (isNaN(x) || isNaN(y)) return setRes(null);
    if (mode === "of") setRes(`${((x / 100) * y).toLocaleString()}`);
    else if (mode === "is") setRes(`${((x / y) * 100).toFixed(2)}%`);
    else setRes(`${(((y - x) / x) * 100).toFixed(2)}%`);
  };

  const labels = {
    of: ["Percentage (%)", "Of value"],
    is: ["Value", "Total"],
    change: ["From", "To"],
  };

  return (
    <div className="space-y-6">
      <Field label="Calculation type">
        <Select data-testid="pct-mode" value={mode} onChange={(e) => { setMode(e.target.value); setRes(null); }}>
          <option value="of">What is X% of Y</option>
          <option value="is">X is what % of Y</option>
          <option value="change">Percentage change (From → To)</option>
        </Select>
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={labels[mode][0]}><TextInput data-testid="pct-a" type="number" value={a} onChange={(e) => setA(e.target.value)} /></Field>
        <Field label={labels[mode][1]}><TextInput data-testid="pct-b" type="number" value={b} onChange={(e) => setB(e.target.value)} /></Field>
      </div>
      <PrimaryButton data-testid="pct-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res !== null && (
        <div data-testid="pct-result"><ResultStat label="Result" value={res} accent /></div>
      )}
    </div>
  );
};

export const GstCalculator = () => {
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState("18");
  const [mode, setMode] = useState("add");
  const [res, setRes] = useState(null);

  const calc = () => {
    const amt = parseFloat(amount);
    const r = parseFloat(rate);
    if (isNaN(amt) || isNaN(r)) return setRes(null);
    const fmt = (x) => x.toLocaleString(undefined, { maximumFractionDigits: 2 });
    if (mode === "add") {
      const tax = (amt * r) / 100;
      setRes({ base: fmt(amt), tax: fmt(tax), total: fmt(amt + tax) });
    } else {
      const base = amt / (1 + r / 100);
      setRes({ base: fmt(base), tax: fmt(amt - base), total: fmt(amt) });
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Amount"><TextInput data-testid="gst-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1000" /></Field>
        <Field label="Tax rate (%)"><TextInput data-testid="gst-rate" type="number" value={rate} onChange={(e) => setRate(e.target.value)} /></Field>
        <Field label="Mode">
          <Select data-testid="gst-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="add">Add tax</option>
            <option value="remove">Remove tax</option>
          </Select>
        </Field>
      </div>
      <PrimaryButton data-testid="gst-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="gst-result" className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ResultStat label="Base Amount" value={res.base} />
          <ResultStat label="Tax" value={res.tax} accent />
          <ResultStat label="Total" value={res.total} />
        </div>
      )}
    </div>
  );
};


export const TipCalculator = () => {
  const [bill, setBill] = useState("");
  const [pct, setPct] = useState("15");
  const [people, setPeople] = useState("1");
  const [res, setRes] = useState(null);
  const calc = () => {
    const b = parseFloat(bill), p = parseFloat(pct), n = parseFloat(people);
    if (isNaN(b) || isNaN(p) || isNaN(n) || n < 1) return setRes(null);
    const tip = (b * p) / 100;
    const total = b + tip;
    const fmt = (x) => x.toLocaleString(undefined, { maximumFractionDigits: 2 });
    setRes({ tip: fmt(tip), total: fmt(total), perPerson: fmt(total / n) });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Bill Amount"><TextInput data-testid="tip-bill" type="number" value={bill} onChange={(e) => setBill(e.target.value)} placeholder="80" /></Field>
        <Field label="Tip %"><TextInput data-testid="tip-pct" type="number" value={pct} onChange={(e) => setPct(e.target.value)} /></Field>
        <Field label="Split between"><TextInput data-testid="tip-people" type="number" value={people} onChange={(e) => setPeople(e.target.value)} /></Field>
      </div>
      <PrimaryButton data-testid="tip-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="tip-result" className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ResultStat label="Tip" value={res.tip} />
          <ResultStat label="Total" value={res.total} accent />
          <ResultStat label="Per Person" value={res.perPerson} />
        </div>
      )}
    </div>
  );
};

export const DiscountCalculator = () => {
  const [price, setPrice] = useState("");
  const [pct, setPct] = useState("");
  const [res, setRes] = useState(null);
  const calc = () => {
    const p = parseFloat(price), d = parseFloat(pct);
    if (isNaN(p) || isNaN(d)) return setRes(null);
    const save = (p * d) / 100;
    const fmt = (x) => x.toLocaleString(undefined, { maximumFractionDigits: 2 });
    setRes({ save: fmt(save), final: fmt(p - save) });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Original Price"><TextInput data-testid="disc-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="120" /></Field>
        <Field label="Discount %"><TextInput data-testid="disc-pct" type="number" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="25" /></Field>
      </div>
      <PrimaryButton data-testid="disc-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="disc-result" className="grid grid-cols-2 gap-4">
          <ResultStat label="You Save" value={res.save} />
          <ResultStat label="Final Price" value={res.final} accent />
        </div>
      )}
    </div>
  );
};

export const SimpleInterest = () => {
  const [p, setP] = useState(""); const [r, setR] = useState(""); const [t, setT] = useState("");
  const [res, setRes] = useState(null);
  const calc = () => {
    const P = parseFloat(p), R = parseFloat(r), T = parseFloat(t);
    if (isNaN(P) || isNaN(R) || isNaN(T)) return setRes(null);
    const si = (P * R * T) / 100;
    const fmt = (x) => x.toLocaleString(undefined, { maximumFractionDigits: 2 });
    setRes({ interest: fmt(si), total: fmt(P + si) });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Principal"><TextInput data-testid="si-p" type="number" value={p} onChange={(e) => setP(e.target.value)} /></Field>
        <Field label="Rate (% / yr)"><TextInput data-testid="si-r" type="number" value={r} onChange={(e) => setR(e.target.value)} /></Field>
        <Field label="Time (years)"><TextInput data-testid="si-t" type="number" value={t} onChange={(e) => setT(e.target.value)} /></Field>
      </div>
      <PrimaryButton data-testid="si-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="si-result" className="grid grid-cols-2 gap-4">
          <ResultStat label="Interest" value={res.interest} accent />
          <ResultStat label="Total Amount" value={res.total} />
        </div>
      )}
    </div>
  );
};

export const CompoundInterest = () => {
  const [p, setP] = useState(""); const [r, setR] = useState(""); const [t, setT] = useState(""); const [n, setN] = useState("1");
  const [res, setRes] = useState(null);
  const calc = () => {
    const P = parseFloat(p), R = parseFloat(r), T = parseFloat(t), N = parseFloat(n);
    if (isNaN(P) || isNaN(R) || isNaN(T) || isNaN(N) || N < 1) return setRes(null);
    const amount = P * Math.pow(1 + R / 100 / N, N * T);
    const fmt = (x) => x.toLocaleString(undefined, { maximumFractionDigits: 2 });
    setRes({ interest: fmt(amount - P), total: fmt(amount) });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Field label="Principal"><TextInput data-testid="ci-p" type="number" value={p} onChange={(e) => setP(e.target.value)} /></Field>
        <Field label="Rate (% / yr)"><TextInput data-testid="ci-r" type="number" value={r} onChange={(e) => setR(e.target.value)} /></Field>
        <Field label="Time (years)"><TextInput data-testid="ci-t" type="number" value={t} onChange={(e) => setT(e.target.value)} /></Field>
        <Field label="Compounds / yr"><TextInput data-testid="ci-n" type="number" value={n} onChange={(e) => setN(e.target.value)} /></Field>
      </div>
      <PrimaryButton data-testid="ci-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="ci-result" className="grid grid-cols-2 gap-4">
          <ResultStat label="Interest Earned" value={res.interest} accent />
          <ResultStat label="Total Amount" value={res.total} />
        </div>
      )}
    </div>
  );
};

export const DateDifference = () => {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [res, setRes] = useState(null);
  const calc = () => {
    if (!from || !to) return setRes(null);
    const d1 = new Date(from), d2 = new Date(to);
    const diff = Math.abs(d2 - d1);
    const days = Math.floor(diff / 86400000);
    setRes({ days: days.toLocaleString(), weeks: Math.floor(days / 7).toLocaleString(), months: Math.floor(days / 30.44).toLocaleString() });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="From date"><TextInput data-testid="dd-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To date"><TextInput data-testid="dd-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      </div>
      <PrimaryButton data-testid="dd-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="dd-result" className="grid grid-cols-3 gap-4">
          <ResultStat label="Days" value={res.days} accent />
          <ResultStat label="Weeks" value={res.weeks} />
          <ResultStat label="Months" value={res.months} />
        </div>
      )}
    </div>
  );
};

const fmt0 = (x) => x.toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmt2 = (x) => x.toLocaleString(undefined, { maximumFractionDigits: 2 });

export const LoanCalculator = () => {
  const [p, setP] = useState(""); const [r, setR] = useState(""); const [n, setN] = useState("");
  const [res, setRes] = useState(null);
  const calc = () => {
    const principal = parseFloat(p), rate = parseFloat(r) / 12 / 100, months = parseFloat(n) * 12;
    if (!principal || !rate || !months) return setRes(null);
    const emi = (principal * rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
    const total = emi * months;
    setRes({ emi: fmt0(emi), total: fmt0(total), interest: fmt0(total - principal) });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Loan Amount"><TextInput data-testid="loan-principal" type="number" value={p} onChange={(e) => setP(e.target.value)} placeholder="250000" /></Field>
        <Field label="Interest Rate (% / yr)"><TextInput data-testid="loan-rate" type="number" value={r} onChange={(e) => setR(e.target.value)} placeholder="7.5" /></Field>
        <Field label="Tenure (years)"><TextInput data-testid="loan-tenure" type="number" value={n} onChange={(e) => setN(e.target.value)} placeholder="10" /></Field>
      </div>
      <PrimaryButton data-testid="loan-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="loan-result" className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ResultStat label="Monthly Payment" value={res.emi} accent />
          <ResultStat label="Total Interest" value={res.interest} />
          <ResultStat label="Total Payment" value={res.total} />
        </div>
      )}
    </div>
  );
};

export const SipCalculator = () => {
  const [m, setM] = useState(""); const [r, setR] = useState(""); const [y, setY] = useState("");
  const [res, setRes] = useState(null);
  const calc = () => {
    const monthly = parseFloat(m), rate = parseFloat(r) / 12 / 100, months = parseFloat(y) * 12;
    if (!monthly || isNaN(rate) || !months) return setRes(null);
    const fv = rate === 0 ? monthly * months : monthly * ((Math.pow(1 + rate, months) - 1) / rate) * (1 + rate);
    const invested = monthly * months;
    setRes({ fv: fmt0(fv), invested: fmt0(invested), gain: fmt0(fv - invested) });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Monthly Investment"><TextInput data-testid="sip-monthly" type="number" value={m} onChange={(e) => setM(e.target.value)} placeholder="5000" /></Field>
        <Field label="Expected Return (% / yr)"><TextInput data-testid="sip-rate" type="number" value={r} onChange={(e) => setR(e.target.value)} placeholder="12" /></Field>
        <Field label="Duration (years)"><TextInput data-testid="sip-years" type="number" value={y} onChange={(e) => setY(e.target.value)} placeholder="10" /></Field>
      </div>
      <PrimaryButton data-testid="sip-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="sip-result" className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ResultStat label="Invested" value={res.invested} />
          <ResultStat label="Est. Returns" value={res.gain} />
          <ResultStat label="Total Value" value={res.fv} accent />
        </div>
      )}
    </div>
  );
};

export const FdCalculator = () => {
  const [p, setP] = useState(""); const [r, setR] = useState(""); const [y, setY] = useState(""); const [n, setN] = useState("4");
  const [res, setRes] = useState(null);
  const calc = () => {
    const P = parseFloat(p), R = parseFloat(r) / 100, T = parseFloat(y), N = parseFloat(n);
    if (!P || isNaN(R) || !T || !N) return setRes(null);
    const maturity = P * Math.pow(1 + R / N, N * T);
    setRes({ maturity: fmt2(maturity), interest: fmt2(maturity - P) });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Field label="Deposit"><TextInput data-testid="fd-principal" type="number" value={p} onChange={(e) => setP(e.target.value)} placeholder="100000" /></Field>
        <Field label="Rate (% / yr)"><TextInput data-testid="fd-rate" type="number" value={r} onChange={(e) => setR(e.target.value)} placeholder="6.5" /></Field>
        <Field label="Years"><TextInput data-testid="fd-years" type="number" value={y} onChange={(e) => setY(e.target.value)} placeholder="5" /></Field>
        <Field label="Compounds / yr"><TextInput data-testid="fd-n" type="number" value={n} onChange={(e) => setN(e.target.value)} /></Field>
      </div>
      <PrimaryButton data-testid="fd-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="fd-result" className="grid grid-cols-2 gap-4">
          <ResultStat label="Interest Earned" value={res.interest} />
          <ResultStat label="Maturity Amount" value={res.maturity} accent />
        </div>
      )}
    </div>
  );
};

export const FuelCostCalculator = () => {
  const [dist, setDist] = useState(""); const [mileage, setMileage] = useState(""); const [price, setPrice] = useState("");
  const [res, setRes] = useState(null);
  const calc = () => {
    const d = parseFloat(dist), m = parseFloat(mileage), pr = parseFloat(price);
    if (!d || !m || isNaN(pr)) return setRes(null);
    const fuel = d / m;
    setRes({ fuel: fmt2(fuel), cost: fmt2(fuel * pr) });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Distance (km)"><TextInput data-testid="fuel-distance" type="number" value={dist} onChange={(e) => setDist(e.target.value)} placeholder="300" /></Field>
        <Field label="Mileage (km / litre)"><TextInput data-testid="fuel-mileage" type="number" value={mileage} onChange={(e) => setMileage(e.target.value)} placeholder="15" /></Field>
        <Field label="Fuel Price (per litre)"><TextInput data-testid="fuel-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="100" /></Field>
      </div>
      <PrimaryButton data-testid="fuel-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="fuel-result" className="grid grid-cols-2 gap-4">
          <ResultStat label="Fuel Needed (L)" value={res.fuel} />
          <ResultStat label="Total Cost" value={res.cost} accent />
        </div>
      )}
    </div>
  );
};

export const SalaryToHourly = () => {
  const [salary, setSalary] = useState(""); const [hours, setHours] = useState("40"); const [weeks, setWeeks] = useState("52");
  const [res, setRes] = useState(null);
  const calc = () => {
    const s = parseFloat(salary), h = parseFloat(hours), w = parseFloat(weeks);
    if (!s || !h || !w) return setRes(null);
    const hourly = s / (h * w);
    setRes({ hourly: fmt2(hourly), monthly: fmt2(s / 12), weekly: fmt2(s / w) });
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Annual Salary"><TextInput data-testid="sth-salary" type="number" value={salary} onChange={(e) => setSalary(e.target.value)} placeholder="60000" /></Field>
        <Field label="Hours / week"><TextInput data-testid="sth-hours" type="number" value={hours} onChange={(e) => setHours(e.target.value)} /></Field>
        <Field label="Weeks / year"><TextInput data-testid="sth-weeks" type="number" value={weeks} onChange={(e) => setWeeks(e.target.value)} /></Field>
      </div>
      <PrimaryButton data-testid="sth-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="sth-result" className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ResultStat label="Hourly" value={res.hourly} accent />
          <ResultStat label="Weekly" value={res.weekly} />
          <ResultStat label="Monthly" value={res.monthly} />
        </div>
      )}
    </div>
  );
};

const gcd = (a, b) => (b ? gcd(b, a % b) : a);

export const FractionCalculator = () => {
  const [n1, setN1] = useState(""); const [d1, setD1] = useState(""); const [n2, setN2] = useState(""); const [d2, setD2] = useState(""); const [op, setOp] = useState("+");
  const [res, setRes] = useState(null);
  const calc = () => {
    const a = parseInt(n1), b = parseInt(d1), c = parseInt(n2), d = parseInt(d2);
    if (isNaN(a) || isNaN(b) || isNaN(c) || isNaN(d) || b === 0 || d === 0) return setRes(null);
    let num, den;
    if (op === "+") { num = a * d + c * b; den = b * d; }
    else if (op === "-") { num = a * d - c * b; den = b * d; }
    else if (op === "×") { num = a * c; den = b * d; }
    else { num = a * d; den = b * c; }
    if (den === 0) return setRes(null);
    const sign = (num < 0) !== (den < 0) ? -1 : 1;
    num = Math.abs(num); den = Math.abs(den);
    const g = gcd(num, den) || 1;
    setRes({ frac: `${sign < 0 ? "-" : ""}${num / g}/${den / g}`, dec: fmt2((sign * num) / den) });
  };
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-20"><Field label="Num"><TextInput data-testid="frac-n1" type="number" value={n1} onChange={(e) => setN1(e.target.value)} placeholder="1" /></Field></div>
        <span className="pb-2.5 text-2xl">/</span>
        <div className="w-20"><Field label="Den"><TextInput data-testid="frac-d1" type="number" value={d1} onChange={(e) => setD1(e.target.value)} placeholder="2" /></Field></div>
        <div className="w-16"><Field label="Op"><Select data-testid="frac-op" value={op} onChange={(e) => setOp(e.target.value)}><option>+</option><option>-</option><option>×</option><option>÷</option></Select></Field></div>
        <div className="w-20"><Field label="Num"><TextInput data-testid="frac-n2" type="number" value={n2} onChange={(e) => setN2(e.target.value)} placeholder="1" /></Field></div>
        <span className="pb-2.5 text-2xl">/</span>
        <div className="w-20"><Field label="Den"><TextInput data-testid="frac-d2" type="number" value={d2} onChange={(e) => setD2(e.target.value)} placeholder="3" /></Field></div>
      </div>
      <PrimaryButton data-testid="frac-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="frac-result" className="grid grid-cols-2 gap-4">
          <ResultStat label="Fraction" value={res.frac} accent />
          <ResultStat label="Decimal" value={res.dec} />
        </div>
      )}
    </div>
  );
};

export const AverageCalculator = () => {
  const [vals, setVals] = useState("");
  const [res, setRes] = useState(null);
  const calc = () => {
    const nums = vals.split(/[\s,]+/).map((x) => parseFloat(x)).filter((x) => !isNaN(x));
    if (!nums.length) return setRes(null);
    const sum = nums.reduce((a, b) => a + b, 0);
    setRes({ mean: fmt2(sum / nums.length), sum: fmt2(sum), count: nums.length, min: fmt2(Math.min(...nums)), max: fmt2(Math.max(...nums)) });
  };
  return (
    <div className="space-y-6">
      <Field label="Numbers (comma or space separated)">
        <TextInput data-testid="avg-input" value={vals} onChange={(e) => setVals(e.target.value)} placeholder="10, 20, 30, 40" />
      </Field>
      <PrimaryButton data-testid="avg-calc-btn" onClick={calc}>Calculate</PrimaryButton>
      {res && (
        <div data-testid="avg-result" className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <ResultStat label="Average" value={res.mean} accent />
          <ResultStat label="Sum" value={res.sum} />
          <ResultStat label="Count" value={res.count} />
          <ResultStat label="Min" value={res.min} />
          <ResultStat label="Max" value={res.max} />
        </div>
      )}
    </div>
  );
};

const SCI_KEYS = [
  ["7", "8", "9", "/", "sin("],
  ["4", "5", "6", "*", "cos("],
  ["1", "2", "3", "-", "tan("],
  ["0", ".", "(", ")", "+"],
  ["√(", "^", "π", "log(", "ln("],
];

export const ScientificCalculator = () => {
  const [expr, setExpr] = useState("");
  const [res, setRes] = useState("");
  const press = (k) => setExpr((e) => e + k);
  const evaluate = () => {
    try {
      let s = expr
        .replace(/π/g, "Math.PI")
        .replace(/√\(/g, "Math.sqrt(")
        .replace(/sin\(/g, "Math.sin(")
        .replace(/cos\(/g, "Math.cos(")
        .replace(/tan\(/g, "Math.tan(")
        .replace(/log\(/g, "Math.log10(")
        .replace(/ln\(/g, "Math.log(")
        .replace(/\^/g, "**");
      if (!/^[0-9+\-*/(). ,MathPIsqrtincoglE**]*$/.test(s)) throw new Error("bad");
      // eslint-disable-next-line no-new-func
      const out = Function(`"use strict"; return (${s})`)();
      if (out === undefined || Number.isNaN(out) || !Number.isFinite(out)) throw new Error("bad");
      setRes(String(Number(out.toFixed(10))));
    } catch { setRes("Error"); }
  };
  return (
    <div data-testid="scientific" className="mx-auto max-w-md space-y-4">
      <div className="border border-border bg-card p-4">
        <input data-testid="sci-expr" value={expr} onChange={(e) => setExpr(e.target.value)} placeholder="0" className="w-full bg-transparent text-right font-mono text-2xl outline-none" />
        <p data-testid="sci-result" className="mt-1 text-right font-heading text-3xl font-black text-primary break-all">{res || "0"}</p>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {SCI_KEYS.flat().map((k) => (
          <button key={k} data-testid={`sci-key-${k}`} onClick={() => press(k)} className="h-11 border border-border bg-card text-sm font-semibold transition-colors hover:bg-muted">{k}</button>
        ))}
        <button data-testid="sci-clear" onClick={() => { setExpr(""); setRes(""); }} className="col-span-1 h-11 border border-border bg-muted text-sm font-semibold transition-colors hover:bg-border">C</button>
        <button data-testid="sci-back" onClick={() => setExpr((e) => e.slice(0, -1))} className="col-span-2 h-11 border border-border bg-card text-sm font-semibold transition-colors hover:bg-muted">⌫</button>
        <button data-testid="sci-equals" onClick={evaluate} className="col-span-2 h-11 bg-primary text-sm font-bold text-white transition-colors hover:opacity-90">=</button>
      </div>
    </div>
  );
};
